import { useMemo, useState } from "react";

import { postTradingOrdersOrderIdClose } from "../api/endpoints/trading/trading";
import type { ClosePositionResult } from "../api/models/closePositionResult";
import type { Order } from "../api/models/order";
import { getBearerToken } from "../lib/authClient";

import "./ClosePositionModal.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

const PERCENT_OPTIONS = [25, 50, 75, 100];

const trimZeros = (v: string) => v.replace(/\.?0+$/, "");

interface ClosePositionModalProps {
  /** Posição aberta (product=FUTURE com linear.closedAt === null) */
  order: Order;
  /** Fechar o modal sem ação */
  onDismiss: () => void;
  /** Fechamento (parcial ou total) executado com sucesso */
  onClosed: (result: ClosePositionResult) => void;
  /** Estado mudou em paralelo (ex.: TP/SL disparou) — refetch + fechar modal */
  onStale: (message: string) => void;
}

export function ClosePositionModal(
  { order, onDismiss, onClosed, onStale }: ClosePositionModalProps,
) {
  const linear = order.linear;
  const remaining = Number(linear?.remainingQty ?? "0");

  const [percent, setPercent] = useState<number>(100);
  const [qtyInput, setQtyInput] = useState<string>("");
  const [qtyError, setQtyError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Quantidade a fechar: input manual tem prioridade sobre o percentual
  const closeQty = useMemo(() => {
    if (qtyInput) {
      const n = Number(qtyInput.replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    }
    return remaining * (percent / 100);
  }, [qtyInput, percent, remaining]);

  // 100% sem input manual ⇒ body {} (fecha TODO o restante no backend)
  const isFullClose = !qtyInput && percent === 100;

  const closeQtyStr = qtyInput
    ? qtyInput.replace(",", ".")
    : trimZeros(closeQty.toFixed(8));

  const remainingAfter = Math.max(0, remaining - closeQty);

  // Estimativa visual de P&L da parcela: (mark − entry) × qty (invertido p/ short)
  const estimatedPnl = useMemo(() => {
    const mark = Number(linear?.markPrice ?? "");
    const entry = Number(linear?.entryPrice ?? "");
    if (!Number.isFinite(mark) || !Number.isFinite(entry) || closeQty <= 0) {
      return null;
    }
    const direction = order.side === "BUY" ? 1 : -1;
    return (mark - entry) * closeQty * direction;
  }, [linear?.markPrice, linear?.entryPrice, closeQty, order.side]);

  const handleQtyChange = (raw: string) => {
    setQtyInput(raw.replace(/[^\d.,]/g, ""));
    setQtyError(null);
  };

  const qtyTooLarge = closeQty > remaining + 1e-12;
  const canSubmit = !submitting && closeQty > 0 && !qtyTooLarge;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setQtyError(null);
    try {
      // Ações usam SEMPRE o internalId (id canônico do DB), não o id da ByBit.
      // O contrato 1.0.45 o expõe direto na ordem.
      const response = await postTradingOrdersOrderIdClose(
        order.internalId,
        isFullClose ? {} : { qty: closeQtyStr },
        authHeaders(),
      );
      if (response.status === 200 && "data" in response.data) {
        onClosed(response.data.data);
        return;
      }
      const body = response.data as {
        message?: string;
        error?: { code?: string };
      };
      const code = body?.error?.code;
      if (code === "E_INVALID_CLOSE_QTY" || code === "E_CLOSE_QTY_TOO_LARGE") {
        setQtyError(
          body?.message ??
            `Quantidade inválida. Máximo: ${linear?.remainingQty ?? "—"} ${order.baseCoin}`,
        );
      } else if (code === "E_NO_OPEN_POSITION" || response.status === 409) {
        onStale(
          body?.message ?? "A posição já foi fechada (ex.: TP/SL disparou).",
        );
      } else if (code === "E_BYBIT_SUBACCOUNT_MISSING") {
        setError("Conta de trading não configurada. Contate o suporte.");
      } else {
        setError(
          body?.message ?? `Falha ao fechar posição (status ${response.status}).`,
        );
      }
    } catch (err) {
      console.error("Erro ao fechar posição:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="closeModal-overlay" role="dialog" aria-modal="true">
      <div className="closeModal-card">
        <div className="closeModal-head">
          <span className="closeModal-title">Fechar posição</span>
          <button
            type="button"
            className="closeModal-x"
            onClick={onDismiss}
            aria-label="Fechar"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="closeModal-position">
          <span className="closeModal-pair">{order.symbol}</span>
          <span
            className={`closeModal-sideBadge ${
              order.side === "BUY"
                ? "closeModal-sideBadge--long"
                : "closeModal-sideBadge--short"
            }`}
          >
            {order.side === "BUY" ? "Long" : "Short"}
            {linear?.leverage ? ` ${linear.leverage}x` : ""}
          </span>
        </div>

        <div className="closeModal-percentGrid">
          {PERCENT_OPTIONS.map((pct) => (
            <button
              key={pct}
              type="button"
              className={`closeModal-percentBtn ${
                !qtyInput && percent === pct
                  ? "closeModal-percentBtn--active"
                  : ""
              }`}
              onClick={() => {
                setPercent(pct);
                setQtyInput("");
                setQtyError(null);
              }}
            >
              {pct}%
            </button>
          ))}
        </div>

        <div className="closeModal-fieldLabel">
          Quantidade ({order.baseCoin})
        </div>
        <div className="closeModal-input">
          <input
            type="text"
            inputMode="decimal"
            placeholder={closeQtyStr}
            value={qtyInput}
            onChange={(e) => handleQtyChange(e.target.value)}
          />
          <span>{order.baseCoin}</span>
        </div>
        {(qtyError || qtyTooLarge) && (
          <div className="closeModal-fieldError">
            {qtyError ??
              `Máximo disponível: ${linear?.remainingQty ?? "—"} ${order.baseCoin}`}
          </div>
        )}

        <div className="closeModal-summary">
          <div className="closeModal-row">
            <span>Quantidade a fechar</span>
            <span>
              {closeQtyStr} {order.baseCoin}
            </span>
          </div>
          <div className="closeModal-row">
            <span>Restante após</span>
            <span>
              {isFullClose ? "0" : trimZeros(remainingAfter.toFixed(8))}{" "}
              {order.baseCoin}
            </span>
          </div>
          <div className="closeModal-row">
            <span>P&L estimado</span>
            <span
              className={
                estimatedPnl !== null && estimatedPnl < 0
                  ? "closeModal-pnl--negative"
                  : "closeModal-pnl--positive"
              }
            >
              {estimatedPnl !== null
                ? `${estimatedPnl >= 0 ? "+" : ""}${estimatedPnl.toFixed(2)} ${order.quoteCoin}`
                : "—"}
            </span>
          </div>
        </div>

        {error && <div className="closeModal-error">{error}</div>}

        <div className="closeModal-actions">
          <button
            type="button"
            className="closeModal-btn closeModal-btn--confirm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? "Fechando..."
              : isFullClose
              ? "Fechar posição"
              : "Fechar parcialmente"}
          </button>
          <button
            type="button"
            className="closeModal-btn closeModal-btn--cancel"
            onClick={onDismiss}
            disabled={submitting}
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
